type StorageMethod = 'localStorage' | 'sessionStorage';

type SafeStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem' | 'clear'>;

const createFallbackStorage = (): SafeStorage => {
  const storage = new Map<string, string>();

  return {
    getItem(key: string) {
      return storage.get(key) ?? null;
    },
    setItem(key: string, value: string) {
      storage.set(key, value);
    },
    removeItem(key: string) {
      storage.delete(key);
    },
    clear() {
      storage.clear();
    },
  };
};

const getBrowserStorage = (storageType: StorageMethod): Storage | undefined => {
  if (typeof window === 'undefined') {
    return undefined;
  }

  try {
    return window[storageType];
  } catch {
    return undefined;
  }
};

export const createSafeStorage = (storageType: StorageMethod): SafeStorage => {
  const fallbackStorage = createFallbackStorage();

  return {
    getItem(key: string) {
      const storage = getBrowserStorage(storageType);

      if (storage) {
        try {
          return storage.getItem.call(storage, key);
        } catch {
          return fallbackStorage.getItem(key);
        }
      }

      return fallbackStorage.getItem(key);
    },
    setItem(key: string, value: string) {
      const storage = getBrowserStorage(storageType);

      if (storage) {
        try {
          storage.setItem.call(storage, key, value);
          return;
        } catch {
          fallbackStorage.setItem(key, value);
          return;
        }
      }

      fallbackStorage.setItem(key, value);
    },
    removeItem(key: string) {
      const storage = getBrowserStorage(storageType);

      if (storage) {
        try {
          storage.removeItem.call(storage, key);
        } catch {
          fallbackStorage.removeItem(key);
        }
        return;
      }

      fallbackStorage.removeItem(key);
    },
    clear() {
      const storage = getBrowserStorage(storageType);

      if (storage) {
        try {
          storage.clear.call(storage);
        } catch {
          fallbackStorage.clear();
        }
        return;
      }

      fallbackStorage.clear();
    },
  };
};
